import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  IconButton,
  Table,
  TableContainer,
  Text,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { BigRational } from 'big-rational-ts';
import BigNumber from 'bignumber.js';
import React, { useState } from 'react';
import { MdSwapHoriz } from 'react-icons/md/index.js';
import { assetToUnit, Balance } from '../blockchain/client';
import { IAssetInPortfolio } from '../types';
import ArrowTableSVG from './atoms/svg/arrowtable-svg';
import InfoSVG from './atoms/svg/infoSVG';
import StatusDot from './atoms/svg/statusDot-svg';
import { MeteraTh } from './atoms/table/MeteraTable';
import PortfolioItem from './portfolio-item';
import { stringToFormattedNumber } from './stringToNumber';
import { themeVariables } from '../theme';

type TokensBreakdownProps = {
  type: 'mint' | 'burn';
  portfolioAssets: IAssetInPortfolio[];
  balance: Balance | null;
  amountsToModify: Record<string, bigint>;
  formattedState: {
    prices: Record<string, BigRational>;
    weights: Record<string, BigRational>;
    state: {
      assets: Record<string, bigint>;
      mtkSupply: bigint;
    };
  };
  hideBreakdown: () => void;
  from: BigNumber;
  to: BigNumber;
  portfolioTicker: string;
  hasInsufficientToken: boolean;
  isModal?: boolean;
  background?: string;
};

const TokensBreakdown: React.FC<TokensBreakdownProps> = ({
  type,
  portfolioAssets,
  balance,
  amountsToModify,
  formattedState,
  hideBreakdown,
  from,
  to,
  portfolioTicker,
  hasInsufficientToken,
  isModal = true,
  background,
}) => (
  <Flex
    width={'full'}
    height={'full'}
    flexDirection={'column'}
    padding="1rem"
    background={background ? background : themeVariables.background.modal}
  >
    <Flex
      alignItems={'flex-end'}
      width={'100%'}
      flexDirection={'column'}
      gap={1}
      padding={'8px'}
    >
      <IconButton
        aria-label="Close"
        borderRadius={'100%'}
        fill={'#a78bfa'}
        color={'#a78bfa;'}
        size={'sm'}
        onClick={hideBreakdown}
        background={'transparent'}
        icon={<CloseIcon />}
        alignSelf={'flex-end'}
      />
      <Flex width={'100%'} justifyContent={'center'} gap={'1rem'}>
        <Flex fontSize="24px" lineHeight="24px" gap={'4px'}>
          <Text fontWeight="500" letterSpacing="-0.02em" color={'#A3A3A3'}>
            {type === 'mint' ? 'ADA' : portfolioTicker}
          </Text>
          {stringToFormattedNumber(from.abs().toString())}
        </Flex>
        <MdSwapHoriz />
        <Flex fontSize="24px" lineHeight="24px" gap={'4px'}>
          <Text fontWeight="500" letterSpacing="-0.02em" color={'#A3A3A3'}>
            {type === 'mint' ? portfolioTicker : 'ADA'}
          </Text>
          {stringToFormattedNumber(
            to
              .abs()
              .div(10 ** 6)
              .toFixed(6),
            4,
          )}
        </Flex>
      </Flex>
      <Flex
        paddingX="0.5rem"
        gap={2}
        width="100%"
        height="2.5rem"
        justifyContent={'center'}
        alignItems={'center'}
      >
        <StatusDot
          showStatus={false}
          status={!hasInsufficientToken ? 'Confirmed' : 'Insufficient'}
        />
        <Text
          fontSize="14px"
          fontWeight="400"
          lineHeight="20px"
          textAlign="left"
        >
          Tokens breakdown
        </Text>
      </Flex>
    </Flex>
    <Box
      overflowY="auto"
      borderRadius={'0.75rem'}
      width="full"
      paddingTop={'0.15rem'}
    >
      <Flex gap={3} overflow="auto">
        <TableContainer
          overflowY="auto"
          sx={{
            '&::-webkit-scrollbar': {
              width: '0px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'none',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'none',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'none',
            },
          }}
        >
          <Table
            variant="unstyled"
            sx={{
              tableLayout: 'fixed',
            }}
          >
            <Thead
              position={'sticky'}
              top={0}
              left={0}
              zIndex={1}
              borderBottom="none"
            >
              <Tr>
                <MeteraTh
                  textAlign="left"
                  paddingX={'8px'}
                  paddingY="1rem"
                  paddingLeft={'10px'}
                  fontSize="0.875rem"
                  color={themeVariables.neutral[400]}
                  borderTopLeftRadius="full"
                >
                  Name
                </MeteraTh>
                <MeteraTh
                  textAlign="right"
                  paddingX={0}
                  paddingY="1rem"
                  fontSize="0.875rem"
                  color={themeVariables.neutral[400]}
                >
                  <Flex gap={2}>
                    <Box>Amount</Box>
                    <Tooltip
                      sx={{
                        background: themeVariables.cards.background,
                        border: `0.5px solid ${themeVariables.cards.stroke}`,
                        borderRadius: '1rem',
                        color: themeVariables.offWhite.offWhite400,
                        fontWeight: '600',
                        padding: '1rem',
                        backdropFilter: 'blur(30px)',
                      }}
                      label={`This is the amount of tokens you${type === 'mint' ? ' need to deposit into' : "'ll receive from"} the instrument. `}
                    >
                      <button>
                        <InfoSVG />
                      </button>
                    </Tooltip>
                  </Flex>
                </MeteraTh>
                <MeteraTh
                  textAlign="right"
                  paddingX={0}
                  paddingY="1rem"
                  fontSize="0.875rem"
                  color={themeVariables.neutral[400]}
                >
                  <Flex width="full" justify="center" gap={2}>
                    <Box>Worth</Box>
                    <Tooltip
                      sx={{
                        background: themeVariables.cards.background,
                        border: `0.5px solid ${themeVariables.cards.stroke}`,
                        borderRadius: '1rem',
                        color: themeVariables.offWhite.offWhite400,
                        fontWeight: '600',
                        padding: '1rem',
                        backdropFilter: 'blur(30px)',
                      }}
                      label="Value in ADA of each asset"
                    >
                      <button>
                        <InfoSVG />
                      </button>
                    </Tooltip>
                  </Flex>
                </MeteraTh>
                {type === 'mint' && (
                  <MeteraTh
                    textAlign="right"
                    paddingX={0}
                    paddingY="1rem"
                    fontSize="0.875rem"
                    color={themeVariables.neutral[400]}
                    paddingRight={'6px'}
                  >
                    Balance
                  </MeteraTh>
                )}
              </Tr>
            </Thead>
            <tbody style={{ maxHeight: '286px' }}>
              {portfolioAssets
                .sort((a, b) => a.asset.ticker.localeCompare(b.asset.ticker))
                .map((assetInPortfolio, i) => (
                  <PortfolioItem
                    item={assetInPortfolio}
                    key={assetInPortfolio.asset.id + i}
                    type={type}
                    balance={balance}
                    amountsToModify={amountsToModify}
                    formattedState={formattedState}
                    {...(isModal ? {} : { fontSize: '12.5px' })}
                  />
                ))}
            </tbody>
          </Table>
        </TableContainer>
      </Flex>
    </Box>
  </Flex>
);

export function TokensBreakdownButton(props: TokensBreakdownProps) {
  const { portfolioAssets, balance, amountsToModify } = props;
  const [showBreakdown, setShowBreakdown] = useState<boolean>(false);

  const hasAllTokens = portfolioAssets.every((item) =>
    BigNumber((amountsToModify[item.asset.id] || 0n).toString()).lte(
      BigNumber((balance?.[assetToUnit(item.asset)] || 0n).toString()),
    ),
  );

  return (
    <Flex
      flexDirection={'column'}
      width={'100%'}
      alignItems={'center'}
      gap={'1rem'}
    >
      <Flex
        paddingX="0.5rem"
        background={themeVariables.background.tableModals}
        border={`1px solid ${themeVariables.cards.stroke}`}
        borderRadius="62rem"
        gap={2}
        width="230px"
        height="2.5rem"
        cursor={'pointer'}
        onClick={() => setShowBreakdown(!showBreakdown)}
        justifyContent={'center'}
        alignItems={'center'}
      >
        <StatusDot
          showStatus={false}
          status={hasAllTokens ? 'Confirmed' : 'Insufficient'}
        />
        <Text
          fontSize="14px"
          fontWeight="400"
          lineHeight="20px"
          textAlign="left"
        >
          {showBreakdown ? 'Hide' : 'View'} token breakdown
        </Text>

        <Box>
          {showBreakdown ? (
            <Box transform="rotate(-90deg)" marginTop="2px">
              <ArrowTableSVG />
            </Box>
          ) : (
            <Box transform="rotate(90deg)" marginTop="4px">
              <ArrowTableSVG />
            </Box>
          )}
        </Box>
      </Flex>
      <TokensBreakdown {...props} />
    </Flex>
  );
}

export default TokensBreakdown;
